/**
 * JessibucaPlayer Component - 2ECOMP Fleet Management
 *
 * Video player using HTTP-FLV via mpegts.js (primary) or WebRTC (fallback).
 *
 * Architecture:
 *   Camera → [JT1078 TCP:10780] → Java Gateway → [FFmpeg: G.711A→AAC, video copy] → [RTMP] → ZLMediaKit → [WS-FLV] → Browser
 *
 * Primary: mpegts.js connects to ZLMediaKit's .live.flv endpoint (H.264+AAC, timestamp corrected)
 * Fallback: WebRTC WHEP to ZLMediaKit (if mpegts.js fails)
 */

import {
  useEffect, useRef, useState, useCallback, memo,
} from 'react';
import {
  Box, CircularProgress, Typography, IconButton, Tooltip, Chip, Stack, Slider,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import VideocamIcon from '@mui/icons-material/Videocam';
import RefreshIcon from '@mui/icons-material/Refresh';
import StopIcon from '@mui/icons-material/Stop';
import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import CloseIcon from '@mui/icons-material/Close';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import StopCircleIcon from '@mui/icons-material/StopCircle';
import mpegts from 'mpegts.js';

import { getStreamUrls } from '../config/m7sConfig';
import { useCameraServer } from '../hooks/useCameraServer';

// Suppress all mpegts.js internal logging (IOController, FLVDemuxer, MSEController, etc.)
// Our error event handler already logs what matters
mpegts.LoggingControl.enableError = false;
mpegts.LoggingControl.enableWarn = false;
mpegts.LoggingControl.enableDebug = false;
mpegts.LoggingControl.enableVerbose = false;

// Configuration
const RECONNECT_DELAY_MS = 2000;
const MAX_RECONNECT_ATTEMPTS = 15;
const MAX_404_RETRIES = 10;
const STALL_CHECK_INTERVAL_MS = 3000;
const STALL_THRESHOLD_MS = 12000;
const STREAM_RECHECK_INTERVAL_MS = 5000;

const PROTOCOL = {
  FLV: 'flv',
  WEBRTC: 'webrtc',
};

/**
 * JessibucaPlayer - HTTP-FLV (mpegts.js) + WebRTC fallback video player
 */
const JessibucaPlayer = ({
  deviceImei,
  channel = 1,
  autoplay = true,
  playbackMode = false,
  remainingSeconds = null,
  onLoad,
  onError,
  onStats,
  onStop,
  onPause,
  onResume,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const cameraServer = useCameraServer();
  const videoRef = useRef(null);
  const playerRef = useRef(null);
  const pcRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const stallCheckRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const retries404Ref = useRef(0);
  const mountedRef = useRef(true);
  const lastPlayTimeRef = useRef(0);
  const lastVideoTimeRef = useRef(0);
  const stallStartRef = useRef(0);
  const protocolRef = useRef(PROTOCOL.FLV);
  const recoveringRef = useRef(false);
  const videoListenersRef = useRef(null);
  const lastStatsUpdateRef = useRef(0);
  const mediaRecorderRef = useRef(null);
  const recordedChunksRef = useRef([]);
  const [recording, setRecording] = useState(false);

  // Stable refs for callback props - prevents useCallback chain invalidation
  const onLoadRef = useRef(onLoad);
  const onErrorRef = useRef(onError);
  const onStatsRef = useRef(onStats);
  const onPauseRef = useRef(onPause);
  const onResumeRef = useRef(onResume);
  onLoadRef.current = onLoad;
  onErrorRef.current = onError;
  onStatsRef.current = onStats;
  onPauseRef.current = onPause;
  onResumeRef.current = onResume;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(true);
  const [volume, setVolume] = useState(0.5);
  const [streamPaused, setStreamPaused] = useState(false);
  const [reconnecting, setReconnecting] = useState(false);
  const [stats, setStats] = useState({ fps: 0, kbps: 0, protocol: 'FLV' });

  // Get URLs for current stream
  const getUrls = useCallback(() => {
    const urls = getStreamUrls(cameraServer.baseUrl, deviceImei, channel);
    return {
      flv: playbackMode ? urls.zlmPlaybackWsFlv : urls.zlmWsFlv,
      webrtc: playbackMode ? urls.playbackWebrtc : urls.zlmWebrtc,
    };
  }, [cameraServer.baseUrl, deviceImei, channel, playbackMode]);

  // Remove video element event listeners
  const removeVideoListeners = useCallback(() => {
    if (videoListenersRef.current && videoRef.current) {
      const {
        timeupdate: htu, playing: hp, pause: hpa, error: he,
      } = videoListenersRef.current;
      if (htu) videoRef.current.removeEventListener('timeupdate', htu);
      videoRef.current.removeEventListener('playing', hp);
      videoRef.current.removeEventListener('pause', hpa);
      videoRef.current.removeEventListener('error', he);
      videoListenersRef.current = null;
    }
  }, []);

  // Cleanup all player resources
  const destroyPlayer = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    if (stallCheckRef.current) {
      clearInterval(stallCheckRef.current);
      stallCheckRef.current = null;
    }
    // Stop recording if active
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
      mediaRecorderRef.current = null;
      setRecording(false);
    }
    removeVideoListeners();
    // Destroy mpegts.js player
    if (playerRef.current) {
      try {
        playerRef.current.pause();
        playerRef.current.unload();
        playerRef.current.detachMediaElement();
        playerRef.current.destroy();
      } catch (e) {
        console.warn('[Player] mpegts cleanup error:', e);
      }
      playerRef.current = null;
    }
    // Close WebRTC connection
    if (pcRef.current) {
      try {
        pcRef.current.close();
      } catch (e) {
        console.warn('[Player] WebRTC cleanup error:', e);
      }
      pcRef.current = null;
    }
    // Detach video
    if (videoRef.current) {
      videoRef.current.srcObject = null;
      videoRef.current.removeAttribute('src');
    }
  }, [removeVideoListeners]);

  // Ref to hold initPlayer for use in scheduleReconnect
  const initPlayerRef = useRef(null);

  // Schedule reconnect (with exponential backoff)
  const scheduleReconnect = useCallback((reason) => {
    if (!mountedRef.current) return;

    // Stop stall detection to prevent cascading reconnects
    if (stallCheckRef.current) {
      clearInterval(stallCheckRef.current);
      stallCheckRef.current = null;
    }
    stallStartRef.current = 0;

    if (reconnectAttemptsRef.current >= MAX_RECONNECT_ATTEMPTS) {
      setError('Conexão perdida - clique para reconectar');
      setReconnecting(false);
      return;
    }

    reconnectAttemptsRef.current += 1;
    const attempt = reconnectAttemptsRef.current;
    // Exponential backoff: 2s, 3s, 4.5s, 6.7s, 10s, 15s (capped)
    const delay = Math.min(RECONNECT_DELAY_MS * Math.pow(1.5, attempt - 1), 15000);

    setReconnecting(true);
    setError(null);

    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }

    reconnectTimeoutRef.current = setTimeout(() => {
      if (mountedRef.current && initPlayerRef.current) {
        initPlayerRef.current();
      }
    }, delay);
  }, [channel]);

  // Initialize mpegts.js FLV player (primary)
  const initFlvPlayer = useCallback(async () => {
    if (!videoRef.current || !deviceImei) return;

    const urls = getUrls();
    if (!mpegts.isSupported()) {
      console.warn('[Player] mpegts.js not supported, falling back to WebRTC');
      protocolRef.current = PROTOCOL.WEBRTC;
      return false;
    }

    const player = mpegts.createPlayer({
      type: 'flv',
      isLive: !playbackMode,
      url: urls.flv,
    }, {
      enableWorker: true,
      enableStashBuffer: true,
      stashInitialSize: playbackMode ? 128 * 1024 : 64 * 1024,
      autoCleanupSourceBuffer: true,
      autoCleanupMaxBackwardDuration: 30,
      autoCleanupMinBackwardDuration: 10,
      lazyLoadMaxDuration: 60,
      seekType: 'range',
      fixAudioTimestampGap: true,
      liveBufferLatencyChasing: !playbackMode,
      liveBufferLatencyChasingOnPaused: false,
      liveBufferLatencyMaxLatency: 2.5,
      liveBufferLatencyMinRemain: 0.8,
    });

    player.attachMediaElement(videoRef.current);

    // Handle mpegts events
    player.on(mpegts.Events.ERROR, (errorType, errorDetail, errorInfo) => {
      // Guard against error loop (MEDIA_ERROR can fire repeatedly before reconnect)
      if (recoveringRef.current) return;
      recoveringRef.current = true;

      // Full cleanup: pause → unload → detach → destroy (kills worker too)
      try {
        player.pause();
        player.unload();
        player.detachMediaElement();
        player.destroy();
      } catch (e) {
        // ignore cleanup errors
      }
      playerRef.current = null;

      // 404 = ZLMediaKit webhook hasn't proxied the stream yet.
      // Retry with a limit to avoid infinite loop.
      if (errorType === mpegts.ErrorTypes.NETWORK_ERROR && errorInfo?.code === 404) {
        retries404Ref.current += 1;
        if (retries404Ref.current > MAX_404_RETRIES) {
          setError('Câmera indisponível no servidor');
          setLoading(false);
          setReconnecting(false);
          return;
        }
        if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = setTimeout(() => {
          if (mountedRef.current && initPlayerRef.current) {
            initPlayerRef.current();
          }
        }, STREAM_RECHECK_INTERVAL_MS);
        return;
      }

      console.warn(`[Player] CH${channel} mpegts error:`, errorType, errorDetail?.code || errorDetail);

      // On network error after a few retries, fall back to WebRTC
      if (errorType === mpegts.ErrorTypes.NETWORK_ERROR && reconnectAttemptsRef.current >= 3) {
        protocolRef.current = PROTOCOL.WEBRTC;
        reconnectAttemptsRef.current = 0;
        scheduleReconnect('FLV network error → WebRTC');
        return;
      }
      scheduleReconnect(`mpegts ${errorType}`);
    });

    player.on(mpegts.Events.STATISTICS_INFO, (statsInfo) => {
      if (!mountedRef.current) return;
      // Throttle stats updates to max once per 2 seconds
      const now = Date.now();
      if (now - lastStatsUpdateRef.current < 2000) return;
      lastStatsUpdateRef.current = now;

      const kbps = Math.round((statsInfo.speed || 0) * 8);
      setStats({
        fps: Math.round(statsInfo.decodedFrames ? (statsInfo.decodedFrames / ((now - (lastPlayTimeRef.current || now)) / 1000)) : 0),
        kbps,
        protocol: 'FLV',
      });
      if (onStatsRef.current) onStatsRef.current({ fps: 0, kbps });
    });

    player.on(mpegts.Events.MEDIA_INFO, (mediaInfo) => {
      // MEDIA_INFO = stream connected and parsed. Clear loading overlay.
      if (mountedRef.current) {
        setPlaying(true);
        setLoading(false);
        setError(null);
        setReconnecting(false);
        reconnectAttemptsRef.current = 0;
        retries404Ref.current = 0;
        lastPlayTimeRef.current = Date.now();
        if (onLoadRef.current) onLoadRef.current();
      }
    });

    playerRef.current = player;

    player.load();
    if (autoplay) {
      videoRef.current.muted = true;
      setMuted(true);
      // Don't await play() - it can block indefinitely on live streams waiting
      // for enough data. The 'playing' event handler (handlePlaying) is the
      // authoritative signal that playback has actually started.
      videoRef.current.play().catch((playErr) => {
        if (playErr.name !== 'AbortError') {
          console.warn(`[Player] CH${channel} Play error:`, playErr.message);
        }
      });
    }

    return true;
  }, [deviceImei, channel, playbackMode, autoplay, getUrls, scheduleReconnect]);

  // Initialize WebRTC player (fallback)
  const initWebrtcPlayer = useCallback(async () => {
    if (!videoRef.current || !deviceImei) return;

    const urls = getUrls();
    const pc = new RTCPeerConnection({
      iceServers: [{ urls: 'stun:stun.l.google.com:19302' }],
    });
    pcRef.current = pc;

    pc.addTransceiver('video', { direction: 'recvonly' });

    pc.ontrack = (event) => {
      if (videoRef.current && event.streams[0]) {
        videoRef.current.srcObject = event.streams[0];
      }
    };

    let iceTimeout = null;
    pc.oniceconnectionstatechange = () => {
      const state = pc.iceConnectionState;
      if (state === 'connected' || state === 'completed') {
        if (iceTimeout) clearTimeout(iceTimeout);
        setLoading(false);
        setReconnecting(false);
        reconnectAttemptsRef.current = 0;
      } else if (state === 'failed' || state === 'disconnected') {
        if (iceTimeout) clearTimeout(iceTimeout);
        scheduleReconnect(`ICE ${state}`);
      }
    };

    iceTimeout = setTimeout(() => {
      if (pc.iceConnectionState !== 'connected' && pc.iceConnectionState !== 'completed') {
        scheduleReconnect('ICE timeout');
      }
    }, 10000);

    const offer = await pc.createOffer();
    await pc.setLocalDescription(offer);

    // Wait for ICE gathering
    await new Promise((resolve) => {
      if (pc.iceGatheringState === 'complete') { resolve(); return; }
      const check = () => {
        if (pc.iceGatheringState === 'complete') {
          pc.removeEventListener('icegatheringstatechange', check);
          resolve();
        }
      };
      pc.addEventListener('icegatheringstatechange', check);
      setTimeout(resolve, 2000);
    });

    const response = await fetch(urls.webrtc, {
      method: 'POST',
      headers: { 'Content-Type': 'application/sdp' },
      body: pc.localDescription.sdp,
    });

    if (!response.ok) {
      throw new Error(`WHEP error: ${response.status}`);
    }

    // ZLMediaKit returns JSON {code, sdp, type}, M7S returns plain SDP
    const contentType = response.headers.get('Content-Type') || '';
    let answerSdp;
    if (contentType.includes('application/json')) {
      const json = await response.json();
      if (json.code !== 0) throw new Error(`WHEP error: ${json.msg || json.code}`);
      answerSdp = json.sdp;
    } else {
      answerSdp = await response.text();
    }

    await pc.setRemoteDescription(new RTCSessionDescription({
      type: 'answer',
      sdp: answerSdp,
    }));

    if (autoplay && videoRef.current) {
      videoRef.current.muted = true;
      setMuted(true);
      try {
        await videoRef.current.play();
      } catch (e) {
        if (e.name !== 'AbortError') {
          console.warn(`[Player] CH${channel} Autoplay failed:`, e.message);
        }
      }
    }

    return true;
  }, [deviceImei, channel, autoplay, getUrls, scheduleReconnect]);

  // Main init function
  const initPlayer = useCallback(async () => {
    if (!videoRef.current || !deviceImei) return;

    destroyPlayer();
    recoveringRef.current = false;
    setLoading(true);
    setError(null);
    setPlaying(false);
    setReconnecting(false);

    try {
      // Connect to ZLMediaKit. The 404 retry logic handles the case
      // where the camera hasn't connected to the gateway yet.

      // Attach video element event listeners BEFORE play() to avoid race condition
      // where 'playing' fires before listener is registered
      const video = videoRef.current;
      let markedPlaying = false;
      const markPlaying = () => {
        if (!mountedRef.current || markedPlaying) return;
        markedPlaying = true;
        setPlaying(true);
        setLoading(false);
        setError(null);
        setReconnecting(false);
        reconnectAttemptsRef.current = 0;
        retries404Ref.current = 0;
        lastPlayTimeRef.current = Date.now();
        if (onLoadRef.current) onLoadRef.current();
      };

      // Use timeupdate as primary signal - fires when currentTime actually
      // advances, which is the most reliable proof of playback with MSE.
      // Guard: currentTime > 0 to avoid false positives when MediaSource is
      // first attached (timeupdate fires with currentTime=0 before any data).
      const handleTimeUpdate = () => {
        if (video.currentTime > 0 && !video.paused) {
          video.removeEventListener('timeupdate', handleTimeUpdate);
          markPlaying();
        }
      };

      const handlePlaying = () => markPlaying();
      const handlePause = () => setPlaying(false);

      const handleError = (e) => {
        if (recoveringRef.current) return;
        console.error(`[Player] CH${channel} Video element error:`, e);
        scheduleReconnect('video error');
      };

      video.addEventListener('timeupdate', handleTimeUpdate);
      video.addEventListener('playing', handlePlaying);
      video.addEventListener('pause', handlePause);
      video.addEventListener('error', handleError);
      videoListenersRef.current = {
        timeupdate: handleTimeUpdate, playing: handlePlaying, pause: handlePause, error: handleError,
      };

      const protocol = protocolRef.current;
      let success = false;

      if (protocol === PROTOCOL.FLV) {
        success = await initFlvPlayer();
        if (success === false) {
          protocolRef.current = PROTOCOL.WEBRTC;
          success = await initWebrtcPlayer();
        }
      } else {
        success = await initWebrtcPlayer();
      }

      if (!success) {
        throw new Error('Failed to initialize player');
      }

      // Track play time for stats
      lastPlayTimeRef.current = Date.now();

      // Stall detection (uses separate ref to avoid mixing video time with Date.now())
      lastVideoTimeRef.current = 0;
      stallStartRef.current = 0;
      stallCheckRef.current = setInterval(() => {
        if (!mountedRef.current || !videoRef.current) return;
        const v = videoRef.current;
        // Skip check if paused, ended, or already reconnecting
        if (v.paused || v.ended || reconnectAttemptsRef.current > 0) return;

        const currentTime = v.currentTime;
        if (currentTime > 0 && currentTime === lastVideoTimeRef.current) {
          // Video time hasn't advanced since last check
          if (!stallStartRef.current) {
            stallStartRef.current = Date.now();
          } else if (Date.now() - stallStartRef.current > STALL_THRESHOLD_MS) {
            console.warn(`[Player] CH${channel} Stall detected`);
            scheduleReconnect('stall detected');
          }
        } else {
          // Video is advancing - reset stall tracking
          lastVideoTimeRef.current = currentTime;
          stallStartRef.current = 0;
        }
      }, STALL_CHECK_INTERVAL_MS);

    } catch (err) {
      console.error(`[Player] CH${channel} Init error:`, err);
      if (reconnectAttemptsRef.current < MAX_RECONNECT_ATTEMPTS) {
        scheduleReconnect('init error');
      } else {
        setError('Falha ao conectar câmera');
        setLoading(false);
      }
      if (onErrorRef.current) onErrorRef.current(err);
    }
  }, [deviceImei, channel, playbackMode, destroyPlayer,
    initFlvPlayer, initWebrtcPlayer, scheduleReconnect]);

  // Keep initPlayerRef in sync
  initPlayerRef.current = initPlayer;

  // Initialize on mount / when stream identity changes / when resuming from pause
  useEffect(() => {
    if (streamPaused) return;
    mountedRef.current = true;
    reconnectAttemptsRef.current = 0;
    protocolRef.current = PROTOCOL.FLV;
    initPlayerRef.current();

    return () => {
      mountedRef.current = false;
      destroyPlayer();
    };
  }, [deviceImei, channel, playbackMode, streamPaused, destroyPlayer]);

  const handleRefresh = useCallback(() => {
    reconnectAttemptsRef.current = 0;
    retries404Ref.current = 0;
    lastVideoTimeRef.current = 0;
    stallStartRef.current = 0;
    protocolRef.current = PROTOCOL.FLV;
    if (initPlayerRef.current) initPlayerRef.current();
  }, []);

  const handleToggleMute = useCallback(() => {
    if (videoRef.current) {
      const newMuted = !videoRef.current.muted;
      videoRef.current.muted = newMuted;
      setMuted(newMuted);
      if (!newMuted) {
        videoRef.current.volume = volume;
      }
    }
  }, [volume]);

  const handleVolumeChange = useCallback((_, newValue) => {
    if (newValue == null || Number.isNaN(newValue)) return;
    const v = newValue / 100;
    setVolume(v);
    if (videoRef.current) {
      videoRef.current.volume = v;
      if (v === 0) {
        videoRef.current.muted = true;
        setMuted(true);
      } else if (videoRef.current.muted) {
        videoRef.current.muted = false;
        setMuted(false);
      }
    }
  }, []);

  const handleTogglePlay = useCallback(() => {
    if (streamPaused) {
      // Resume: send requestVideo + reinit player
      setStreamPaused(false);
      if (onResumeRef.current) onResumeRef.current();
      // initPlayer will be triggered by useEffect (streamPaused changed)
    } else {
      // Pause: stop stream to save bandwidth
      setStreamPaused(true);
      destroyPlayer();
      setPlaying(false);
      setLoading(false);
      setStats({ fps: 0, kbps: 0, protocol: stats.protocol });
      if (onPauseRef.current) onPauseRef.current();
    }
  }, [streamPaused, destroyPlayer, stats.protocol]);

  const handleScreenshot = useCallback(() => {
    if (!videoRef.current || !playing) return;
    const video = videoRef.current;
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth || video.clientWidth;
    canvas.height = video.videoHeight || video.clientHeight;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    canvas.toBlob((blob) => {
      if (!blob) return;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const ts = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
      a.download = `${deviceImei}-CH${channel}-${ts}.png`;
      a.click();
      URL.revokeObjectURL(url);
    }, 'image/png');
  }, [playing, deviceImei, channel]);

  const startRecording = useCallback(() => {
    if (!videoRef.current || !playing) return;
    const stream = videoRef.current.captureStream();
    const mimeType = MediaRecorder.isTypeSupported('video/webm;codecs=vp8,opus')
      ? 'video/webm;codecs=vp8,opus'
      : 'video/webm';
    const recorder = new MediaRecorder(stream, { mimeType });
    recordedChunksRef.current = [];

    recorder.ondataavailable = (e) => {
      if (e.data.size > 0) recordedChunksRef.current.push(e.data);
    };

    recorder.onstop = () => {
      const blob = new Blob(recordedChunksRef.current, { type: mimeType });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const ts = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
      a.download = `${deviceImei}-CH${channel}-${ts}.webm`;
      a.click();
      URL.revokeObjectURL(url);
      recordedChunksRef.current = [];
    };

    recorder.start(1000);
    mediaRecorderRef.current = recorder;
    setRecording(true);
  }, [playing, deviceImei, channel]);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
      mediaRecorderRef.current = null;
    }
    setRecording(false);
  }, []);

  const handleFullscreen = useCallback(() => {
    if (videoRef.current) {
      if (videoRef.current.requestFullscreen) {
        videoRef.current.requestFullscreen();
      } else if (videoRef.current.webkitRequestFullscreen) {
        videoRef.current.webkitRequestFullscreen();
      }
    }
  }, []);

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        position: 'relative',
        bgcolor: '#000',
        overflow: 'hidden',
      }}
    >
      {/* Video element */}
      <video
        ref={videoRef}
        muted={muted}
        autoPlay
        playsInline
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'contain',
          backgroundColor: '#000',
        }}
      />

      {/* Loading/Reconnecting overlay */}
      {(loading || reconnecting) && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'rgba(0,0,0,0.8)',
            zIndex: 10,
          }}
        >
          <VideocamIcon sx={{ fontSize: 32, color: 'rgba(255,255,255,0.5)', mb: 1 }} />
          <CircularProgress size={24} sx={{ color: 'white', mb: 1 }} />
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
            {reconnecting ? 'Reconectando...' : 'Conectando câmera...'}
          </Typography>
        </Box>
      )}

      {/* Error overlay */}
      {error && !loading && !reconnecting && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'rgba(0,0,0,0.9)',
            zIndex: 10,
          }}
        >
          <Typography variant="body2" color="error" sx={{ mb: 2, textAlign: 'center', px: 2 }}>
            {error}
          </Typography>
          <Tooltip title="Tentar novamente">
            <IconButton onClick={handleRefresh} sx={{ color: 'white' }}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      )}

      {/* Stream paused overlay */}
      {streamPaused && (
        <Box
          onClick={handleTogglePlay}
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'rgba(0,0,0,0.7)',
            zIndex: 10,
            cursor: 'pointer',
            '&:hover .resume-icon': {
              transform: 'scale(1.1)',
              bgcolor: alpha(theme.palette.success.main, 0.3),
            },
          }}
        >
          <Box
            className="resume-icon"
            sx={{
              width: 56,
              height: 56,
              borderRadius: '50%',
              bgcolor: 'rgba(255,255,255,0.1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.2s ease',
              mb: 1,
            }}
          >
            <PlayArrowIcon sx={{ fontSize: 32, color: 'rgba(255,255,255,0.85)' }} />
          </Box>
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.7rem' }}>
            Stream pausado
          </Typography>
        </Box>
      )}

      {/* Protocol badge (top-left, minimal) */}
      {playing && (
        <Chip
          label={stats.protocol || protocolRef.current.toUpperCase()}
          size="small"
          sx={{
            position: 'absolute',
            top: 6,
            left: 6,
            height: isMobile ? 22 : 18,
            fontSize: isMobile ? '0.65rem' : '0.55rem',
            bgcolor: protocolRef.current === PROTOCOL.FLV ? alpha(theme.palette.success.main, 0.8) : alpha(theme.palette.info.main, 0.8),
            color: 'white',
            zIndex: 5,
          }}
        />
      )}

      {/* Timer badge (top-right) */}
      {remainingSeconds !== null && playing && (
        <Chip
          label={`${Math.floor(remainingSeconds / 60)}:${String(remainingSeconds % 60).padStart(2, '0')}`}
          size="small"
          sx={{
            position: 'absolute',
            top: 6,
            right: 6,
            height: isMobile ? 22 : 18,
            fontSize: isMobile ? '0.65rem' : '0.55rem',
            fontWeight: 700,
            fontVariantNumeric: 'tabular-nums',
            bgcolor: remainingSeconds <= 60
              ? alpha(theme.palette.error.main, 0.85)
              : alpha(theme.palette.info.main, 0.7),
            color: 'white',
            zIndex: 5,
          }}
        />
      )}

      {/* REC badge (top-right, next to timer) */}
      {recording && (
        <Chip
          icon={<FiberManualRecordIcon sx={{ fontSize: 8, color: theme.palette.error.main }} />}
          label="REC"
          size="small"
          sx={{
            position: 'absolute',
            top: 6,
            right: remainingSeconds !== null && playing ? 70 : 6,
            height: isMobile ? 22 : 18,
            fontSize: isMobile ? '0.65rem' : '0.55rem',
            fontWeight: 700,
            bgcolor: alpha(theme.palette.error.main, 0.85),
            color: 'white',
            zIndex: 5,
            animation: 'pulse 1.5s infinite',
            '@keyframes pulse': { '0%, 100%': { opacity: 1 }, '50%': { opacity: 0.5 } },
          }}
        />
      )}

      {/* Bottom control bar - MettaX style */}
      {!loading && !error && !streamPaused && (
        <Box
          sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            background: 'linear-gradient(0deg, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.4) 70%, transparent 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 1,
            py: 0.5,
            zIndex: 5,
            minHeight: 32,
          }}
        >
          {/* Left: device info + bitrate */}
          <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              variant="caption"
              noWrap
              sx={{ color: 'rgba(255,255,255,0.7)', fontSize: isMobile ? '0.7rem' : '0.65rem', minWidth: 0 }}
            >
              {isMobile ? `CH${channel}` : `${deviceImei}-CH${channel}`}
            </Typography>
            {stats.kbps > 0 && (
              <Typography
                variant="caption"
                sx={{ color: 'rgba(255,255,255,0.5)', fontSize: isMobile ? '0.65rem' : '0.6rem', whiteSpace: 'nowrap' }}
              >
                {(stats.kbps / 8).toFixed(1)}KB/s
              </Typography>
            )}
          </Stack>

          {/* Right: controls */}
          <Stack direction="row" spacing={0} alignItems="center">
            {/* Volume: mute button + slider (hover on desktop, always visible on mobile) */}
            <Stack
              direction="row"
              alignItems="center"
              sx={{
                ...(!isMobile && {
                  '&:hover .volume-slider': { width: 60, opacity: 1, mx: 0.5 },
                }),
              }}
            >
              <Tooltip title={muted ? 'Ativar som' : 'Mudo'}>
                <IconButton size="small" onClick={handleToggleMute} sx={{ color: 'rgba(255,255,255,0.8)', p: isMobile ? 1 : 0.5, minWidth: isMobile ? 44 : 'auto', minHeight: isMobile ? 44 : 'auto' }}>
                  {muted ? <VolumeOffIcon sx={{ fontSize: isMobile ? 22 : 18 }} /> : <VolumeUpIcon sx={{ fontSize: isMobile ? 22 : 18 }} />}
                </IconButton>
              </Tooltip>
              <Slider
                className="volume-slider"
                size="small"
                min={0}
                max={100}
                value={muted ? 0 : Math.round(volume * 100)}
                onChange={handleVolumeChange}
                sx={{
                  width: isMobile ? 50 : 0,
                  opacity: isMobile ? 1 : 0,
                  transition: 'width 0.2s ease, opacity 0.2s ease, margin 0.2s ease',
                  color: 'rgba(255,255,255,0.8)',
                  p: 0,
                  mx: isMobile ? 0.5 : 0,
                  '& .MuiSlider-thumb': { width: isMobile ? 14 : 10, height: isMobile ? 14 : 10 },
                  '& .MuiSlider-rail': { opacity: 0.3 },
                }}
              />
            </Stack>
            <Tooltip title={playing ? 'Pausar' : 'Reproduzir'}>
              <IconButton size="small" onClick={handleTogglePlay} sx={{ color: 'rgba(255,255,255,0.8)', p: isMobile ? 1 : 0.5, minWidth: isMobile ? 44 : 'auto', minHeight: isMobile ? 44 : 'auto' }}>
                {playing ? <PauseIcon sx={{ fontSize: isMobile ? 22 : 18 }} /> : <PlayArrowIcon sx={{ fontSize: isMobile ? 22 : 18 }} />}
              </IconButton>
            </Tooltip>
            <Tooltip title="Screenshot">
              <IconButton
                size="small"
                onClick={handleScreenshot}
                disabled={!playing}
                sx={{
                  color: 'rgba(255,255,255,0.8)',
                  p: isMobile ? 1 : 0.5,
                  minWidth: isMobile ? 44 : 'auto',
                  minHeight: isMobile ? 44 : 'auto',
                }}
              >
                <CameraAltIcon sx={{ fontSize: isMobile ? 22 : 18 }} />
              </IconButton>
            </Tooltip>
            <Tooltip title={recording ? 'Parar gravacao' : 'Gravar'}>
              <IconButton
                size="small"
                onClick={recording ? stopRecording : startRecording}
                disabled={!playing}
                sx={{
                  color: recording ? theme.palette.error.main : 'rgba(255,255,255,0.8)',
                  p: isMobile ? 1 : 0.5,
                  minWidth: isMobile ? 44 : 'auto',
                  minHeight: isMobile ? 44 : 'auto',
                  animation: recording ? 'pulse 1.5s infinite' : 'none',
                  '@keyframes pulse': {
                    '0%, 100%': { opacity: 1 },
                    '50%': { opacity: 0.5 },
                  },
                }}
              >
                {recording
                  ? <StopCircleIcon sx={{ fontSize: isMobile ? 22 : 18 }} />
                  : <FiberManualRecordIcon sx={{ fontSize: isMobile ? 22 : 18 }} />}
              </IconButton>
            </Tooltip>
            <Tooltip title="Atualizar">
              <IconButton size="small" onClick={handleRefresh} sx={{ color: 'rgba(255,255,255,0.8)', p: isMobile ? 1 : 0.5, minWidth: isMobile ? 44 : 'auto', minHeight: isMobile ? 44 : 'auto' }}>
                <RefreshIcon sx={{ fontSize: isMobile ? 22 : 18 }} />
              </IconButton>
            </Tooltip>
            {onStop && (
              <Tooltip title="Fechar stream">
                <IconButton size="small" onClick={onStop} sx={{ color: 'rgba(255,255,255,0.8)', p: isMobile ? 1 : 0.5, minWidth: isMobile ? 44 : 'auto', minHeight: isMobile ? 44 : 'auto', '&:hover': { color: theme.palette.error.main } }}>
                  <CloseIcon sx={{ fontSize: isMobile ? 22 : 18 }} />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title="Tela cheia">
              <IconButton size="small" onClick={handleFullscreen} sx={{ color: 'rgba(255,255,255,0.8)', p: isMobile ? 1 : 0.5, minWidth: isMobile ? 44 : 'auto', minHeight: isMobile ? 44 : 'auto' }}>
                <FullscreenIcon sx={{ fontSize: isMobile ? 22 : 18 }} />
              </IconButton>
            </Tooltip>
          </Stack>
        </Box>
      )}
    </Box>
  );
};

// Memoize to prevent unnecessary re-renders
export default memo(JessibucaPlayer, (prevProps, nextProps) => (
  prevProps.deviceImei === nextProps.deviceImei
  && prevProps.channel === nextProps.channel
  && prevProps.autoplay === nextProps.autoplay
  && prevProps.playbackMode === nextProps.playbackMode
  && prevProps.remainingSeconds === nextProps.remainingSeconds
));
